// Shared market-data layer (Phase: data-layer centralization).
// ONE pipe for candles + spot prices. All App.js consumers (candle cache, dashboard
// chart, More Info chart, day-change, price poller, scoring scan's DATA PATH) read
// through getCandles()/getSpotPrice() instead of issuing their own axios calls.
//
// What this layer adds:
//   1. Request queue — every network call goes through requestQueue.enqueue(), so
//      total volume respects each provider's rate ceiling (see requestQueue.js).
//   2. TTL cache + in-flight dedupe — identical (symbol × granularity × variant)
//      requests within the TTL share ONE network call. Concurrent callers share the
//      same in-flight promise. Failed fetches are evicted immediately (never cached).
//
// What this layer does NOT do: transform data. It returns EXACTLY what each call
// site received before (Coinbase res.data / fetchAlpacaCandles output / spot amount
// string) — consumers keep their own normalizeCandles/formatCandles/parseFloat logic,
// so downstream math (including the scoring engine) sees byte-identical shapes.
import axios from "axios";
import {
  getProvider,
  fetchCoinbaseCandles,
  fetchAlpacaCandles,
  ALPACA_DISPLAY_LOOKBACK_DAYS,
} from "./candles";
import { enqueue } from "./requestQueue";

// Default TTLs. Candles: 45s — under the scoring scan's 60s cadence (each scan still
// fetches fresh data) while letting the 120s candle-cache loop and any chart opens
// inside the window reuse the same response. Spot: 8s — under the 10s price poller.
const DEFAULT_CANDLES_TTL_MS = 45000;
const DEFAULT_SPOT_TTL_MS = 8000;

const cache = new Map(); // key -> { at: ms epoch, promise }

function cachedFetch(key, ttlMs, fetcher) {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < ttlMs) return hit.promise;
  const promise = fetcher();
  const entry = { at: Date.now(), promise };
  cache.set(key, entry);
  promise.catch(() => {
    // Never serve a failure for the rest of the TTL — evict so the next caller retries.
    if (cache.get(key) === entry) cache.delete(key);
  });
  return promise;
}

// Fetch candles for symbol at granularitySecs, provider-routed via getProvider.
// opts:
//   ttlMs   — cache TTL override (e.g. the 5s dashboard poller passes a short TTL).
//   limit   — recent-N candles (day-change passes 2). Preserved verbatim in the
//             Coinbase URL (&limit=N) and as fetchAlpacaCandles' limit arg.
//   display — display-chart depth: Coinbase pages backwards (fetchCoinbaseCandles),
//             Alpaca uses the bigger ALPACA_DISPLAY_LOOKBACK_DAYS window. Cached
//             under its own variant key so it never mixes with scoring-path data.
//   priority— queue's display-chart lane (dispatched before scan/cache requests).
//             Defaults to `display` — a display fetch is by definition user-facing.
// Returns a shallow copy so one consumer's reverse()/sort() can never corrupt the
// cached array another consumer (e.g. the scoring scan) is about to read.
export function getCandles(symbol, granularitySecs, opts = {}) {
  const { ttlMs = DEFAULT_CANDLES_TTL_MS, limit, display = false, priority = display } = opts;
  const variant = display ? "display" : limit != null ? `limit${limit}` : "std";
  const key = `candles|${symbol}|${granularitySecs}|${variant}`;
  return cachedFetch(key, ttlMs, async () => {
    if (getProvider(symbol) === "coinbase") {
      if (display) return fetchCoinbaseCandles(symbol, granularitySecs, priority);
      const url =
        `https://api.exchange.coinbase.com/products/${symbol}/candles?granularity=${granularitySecs}` +
        (limit != null ? `&limit=${limit}` : "");
      const res = await enqueue("coinbase-exchange", () => axios.get(url), { priority });
      return res.data;
    }
    if (display) {
      return fetchAlpacaCandles(symbol, granularitySecs, undefined, ALPACA_DISPLAY_LOOKBACK_DAYS[granularitySecs], priority);
    }
    return fetchAlpacaCandles(symbol, granularitySecs, limit, undefined, priority);
  }).then((raw) => (Array.isArray(raw) ? raw.slice() : raw));
}

// Coinbase spot price. Returns the raw amount STRING (res.data.data.amount) exactly
// as call sites received it before — they keep their own parseFloat/toFixed.
export function getSpotPrice(symbol, opts = {}) {
  const { ttlMs = DEFAULT_SPOT_TTL_MS } = opts;
  const key = `spot|${symbol}`;
  return cachedFetch(key, ttlMs, async () => {
    const res = await enqueue("coinbase-spot", () =>
      axios.get(`https://api.coinbase.com/v2/prices/${symbol}/spot`)
    );
    return res.data.data.amount;
  });
}
